import { defaultTo, memoize } from 'lodash';
import {
  PricingTiers as PricingTiersType,
  PricingPlan,
  BillingFrequency,
} from '@css-xp/graphtypes/src/generated-types-new';
import { ColGroup, SearchResultColumn, TableBody, TableHead } from '../../../search-results-table';
import { CustomDynamicResultTable } from '../../../custom-dynamic-result-table';
import React, { useMemo } from 'react';
import { NoDataContentInTable } from '../../../no-data-content-table';
import './pricing-tiers.scss';

interface Props {
  pricingPlan?: PricingPlan | null;
  activeUsers?: number | null;
  availablePricingPlanTiers?: PricingTiersType[];
  selectedPricingBillingFrequency?: BillingFrequency;
  viewTierDetails?: boolean;
  isInvoicePage?: boolean;
  hidePricingCalculatorText?: boolean;
  // hideCurrencyFromValues is used to hide the currency from the values in the pricing tiers table and show it in headers
  hideCurrencyFromValues?: boolean;
  // shouldAlignAmountCellsRight is used to align the cells in th and tr, which show numbers, to the right.
  // Works in conjunction with hideCurrencyFromValues.
  shouldAlignAmountCellsRight?: boolean;
}

const getColumns = memoize(
  (
    periodUnit: BillingFrequency | undefined,
    hideCurrencyFromValues?: boolean,
    shouldAlignAmountCellsRight?: boolean
  ): SearchResultColumn[] => {
    const flatAmountHeader = (
      <th className={`${shouldAlignAmountCellsRight ? 'text-align-right' : ''}`}>
        Flat amount
        <br />
        (USD)
      </th>
    );

    if (periodUnit === BillingFrequency.MONTHLY) {
      return [
        {
          name: 'Number of users',
          customClassNames: 'col-40',
        },
        {
          name: 'USD (per user)',
          customClassNames: 'col-30',
          ...(hideCurrencyFromValues && {
            renderComponent: (
              <th className={`${shouldAlignAmountCellsRight ? 'text-align-right' : ''}`}>
                USD
                <br />
                (per user)
              </th>
            ),
          }),
        },
        {
          name: 'Flat amount',
          customClassNames: 'col-30',
          ...(hideCurrencyFromValues && {
            renderComponent: flatAmountHeader,
          }),
        },
      ];
    }

    return [
      {
        name: 'Number of users',
        customClassNames: 'col-50',
      },
      {
        name: 'Flat amount',
        customClassNames: 'col-50',
        ...(hideCurrencyFromValues && {
          renderComponent: flatAmountHeader,
        }),
      },
    ];
  }
);

export const PricingTiers = ({
  pricingPlan,
  activeUsers,
  availablePricingPlanTiers,
  selectedPricingBillingFrequency,
  viewTierDetails,
  isInvoicePage = false,
  hidePricingCalculatorText = false,
  hideCurrencyFromValues,
  shouldAlignAmountCellsRight,
}: Props) => {
  const emptyStateProps = {
    header: '',
    description: 'No pricing tiers found',
  };

  const pricingDetails = pricingPlan;

  const sortedPricingDetails = useMemo(() => {
    return (pricingDetails?.tiers ?? []).slice().sort((tier1: any, tier2: any) => tier1.floor - tier2.floor);
  }, []);

  const changePricingPlanTiers = (availablePricingPlanTiers ?? [])
    .slice()
    .sort((tier1: any, tier2: any) => tier1.floor - tier2.floor);

  //This is to conditionally render the tiers on manage pane vs view pricing details on entitlement summary
  const showSelectedPricingTiers = viewTierDetails ? changePricingPlanTiers : sortedPricingDetails;

  const periodUnit = pricingDetails?.billingFrequency ?? selectedPricingBillingFrequency;

  const users = activeUsers ?? 0;
  const pricingPlanType = pricingPlan?.pricingPlanType;
  const renderActionsOnTop = React.useCallback(() => {
    if (pricingPlanType !== 'ADVANTAGE_PRICING' && !isInvoicePage && !hidePricingCalculatorText) {
      return [
        () => (
          <div>
            You can estimate the list price cost of products with our{' '}
            <a href="https://www.atlassian.com/software/pricing-calculator" target="_blank" rel="noreferrer">
              pricing calculator
            </a>
            .
          </div>
        ),
      ];
    }
    return undefined;
  }, [pricingPlanType, isInvoicePage]);

  const columns = getColumns(periodUnit, hideCurrencyFromValues, shouldAlignAmountCellsRight);

  return (
    <div className="pricing-tiers-table">
      <CustomDynamicResultTable
        heading={'Pricing tiers'}
        emptyStateProps={emptyStateProps}
        noResults={showSelectedPricingTiers.length <= 0}
        isLoading={false}
        error={''}
        testId={'pricing-tiers-table'}
        className="tier-details"
        actions={renderActionsOnTop()}
      >
        <ColGroup columns={columns} />
        <TableHead columns={columns} />
        <TableBody>
          {showSelectedPricingTiers.map((tier: any, index: any) => (
            <React.Fragment key={index}>
              <tr
                className={`pricing-details line-item
                ${
                  tier?.ceiling === null
                    ? users >= tier?.floor
                      ? ' highlight-row'
                      : ' no-highlight'
                    : users >= tier?.floor && users <= tier?.ceiling
                    ? ' highlight-row'
                    : ' no-highlight'
                } 
                `}
              >
                <td>{tier?.ceiling === null ? tier?.floor + ' +' : tier?.floor + ' - ' + tier?.ceiling}</td>
                {periodUnit === BillingFrequency.MONTHLY && (
                  <td className={`${shouldAlignAmountCellsRight ? 'text-align-right' : ''}`}>
                    {tier.unitAmount ? (
                      <div>{`${hideCurrencyFromValues ? '' : 'USD '}${(
                        Math.abs(defaultTo(tier.unitAmount, 0)) / 100
                      ).toFixed(2)}`}</div>
                    ) : (
                      <NoDataContentInTable />
                    )}
                  </td>
                )}
                <td className={`${shouldAlignAmountCellsRight ? 'text-align-right' : ''}`}>
                  {tier.flatAmount ? (
                    <div>{`${hideCurrencyFromValues ? '' : 'USD '}${(
                      Math.abs(defaultTo(tier.flatAmount, 0)) / 100
                    ).toFixed(2)}`}</div>
                  ) : (
                    <NoDataContentInTable />
                  )}
                </td>
              </tr>
            </React.Fragment>
          ))}
        </TableBody>
      </CustomDynamicResultTable>
    </div>
  );
};
